import type {
  MaybeUnknown,
  ResolutionReason,
  RevertBlockedReason,
  RuntimeOutcomeReason,
} from '../models/insightActionReason'
import { INSIGHT_ACTION_ERROR_CODES } from '../models/insightActionError'

const NAMESPACE = 'insightActions.reason'

export function camelize(code: string): string {
  return code.toLowerCase().replaceAll(/_(.)/g, (_match, char: string) => char.toUpperCase())
}

export const RESOLUTION_REASONS: readonly ResolutionReason[] = [
  'filtered_out',
  'unmapped_channel',
  'no_registered_action',
  'platform_not_writable',
  'not_applicable',
  'deferred',
  'ambiguous_row_key',
  'target_unresolved',
  'missing_payload_value',
  'invalid_payload',
]

export const RUNTIME_REASONS: readonly RuntimeOutcomeReason[] = [
  'already_requested',
  'awaiting_approval',
  'guardrail_blocked',
  'ambiguous_account',
  'target_locked',
  'platform_refused',
  'blocked_by_pending_item',
  'timeout',
  'unexpected_error',
  'execute_failed',
  'partially_applied',
  'rejected',
  'expired',
  'cancelled',
  'revert_not_eligible',
  'action_not_reversible',
  'revert_awaiting_approval',
  'revert_timeout',
  'already_decided',
]

const CHIP_TAILS: readonly ResolutionReason[] = [
  'no_registered_action',
  'platform_not_writable',
  'not_applicable',
  'deferred',
  'unmapped_channel',
]

const REVERT_BLOCKS: readonly RevertBlockedReason[] = [
  'awaiting_approval',
  'not_succeeded',
  'no_request',
  'not_reversible',
]

const RESOLUTION = new Set<string>(RESOLUTION_REASONS)
const RUNTIME = new Set<string>(RUNTIME_REASONS)
const CHIPS = new Set<string>(CHIP_TAILS)
const BLOCKS = new Set<string>(REVERT_BLOCKS)
const ERRORS = new Set<string>(INSIGHT_ACTION_ERROR_CODES)

export interface ReasonKeys {
  label: string
  description: string
  unknown: boolean
}

const UNKNOWN: ReasonKeys = {
  label: `${NAMESPACE}.unknown.label`,
  description: `${NAMESPACE}.unknown.description`,
  unknown: true,
}

export function reasonKeys(
  code?: MaybeUnknown<ResolutionReason | RuntimeOutcomeReason> | null,
): ReasonKeys | null {
  if (!code) return null
  const group = RESOLUTION.has(code) ? 'resolution' : RUNTIME.has(code) ? 'runtime' : null
  if (!group) return UNKNOWN
  const base = `${NAMESPACE}.${group}.${camelize(code)}`
  return { label: `${base}.label`, description: `${base}.description`, unknown: false }
}

export function chipTailKey(code?: MaybeUnknown<ResolutionReason> | null): string {
  return code && CHIPS.has(code)
    ? `${NAMESPACE}.chipTail.${camelize(code)}`
    : `${NAMESPACE}.chipTail.blocked`
}

export function revertBlockedKey(code?: RevertBlockedReason | null): string {
  if (!code) return `${NAMESPACE}.revertBlocked.notReversible`
  return BLOCKS.has(code)
    ? `${NAMESPACE}.revertBlocked.${camelize(code)}`
    : `${NAMESPACE}.revertBlocked.unknown`
}

export function errorKey(code?: string | null): string {
  return code && ERRORS.has(code)
    ? `insightActions.error.${camelize(code)}`
    : 'insightActions.error.generic'
}
